/**
 * Module: Card Swiper Slider
 * Description: Inizializza gli slider condivisi delle sezioni News e Report.
 */

const DEFAULT_AUTOPLAY_DELAY = 5000;
const DEFAULT_TRANSITION_SPEED = 600;

const getPositiveInteger = (value, fallback) => {
	const parsedValue = Number.parseInt(value, 10);

	return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : fallback;
};

export function initCardSlider() {
	const sliders = document.querySelectorAll('[data-card-slider]');

	if (!sliders.length) {
		return;
	}

	if (typeof window.Swiper !== 'function') {
		console.warn('Swiper non disponibile per gli slider News e Report.');
		return;
	}

	const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

	sliders.forEach((slider) => {
		const section = slider.closest('[data-card-slider-section]');
		const slidesCount = slider.querySelectorAll('.swiper-slide').length;

		if (!section || slidesCount === 0 || slider.dataset.swiperInitialized === 'true') {
			return;
		}

		const prevEl = section.querySelector('[data-card-slider-prev]');
		const nextEl = section.querySelector('[data-card-slider-next]');
		const paginationEl = section.querySelector('[data-card-slider-pagination]');

		if (!prevEl || !nextEl || !paginationEl) {
			return;
		}

		const autoplayDelay = getPositiveInteger(
			slider.dataset.cardSliderAutoplay,
			DEFAULT_AUTOPLAY_DELAY
		);
		const transitionSpeed = getPositiveInteger(
			slider.dataset.cardSliderSpeed,
			DEFAULT_TRANSITION_SPEED
		);

		let swiper = null;

		const updateControlsVisibility = () => {
			const hideNavigation = !swiper || swiper.isLocked || slidesCount <= 1;

			section.classList.toggle('is-slider-locked', hideNavigation);
			section.classList.toggle('is-slider-ready', Boolean(swiper));
			prevEl.hidden = hideNavigation;
			nextEl.hidden = hideNavigation;
			paginationEl.hidden = hideNavigation;
		};

		const stopAutoplay = () => {
			if (!swiper || !swiper.autoplay || !swiper.autoplay.running) {
				return;
			}

			swiper.autoplay.stop();
		};

		const pauseAutoplay = () => {
			if (
				!swiper
				|| !swiper.autoplay
				|| !swiper.autoplay.running
				|| swiper.autoplay.paused
			) {
				return;
			}

			swiper.autoplay.pause();
		};

		const shouldPauseAutoplay = () => (
			document.hidden
			|| section.contains(document.activeElement)
			|| slider.matches(':hover')
		);

		const reconcileAutoplay = () => {
			if (!swiper || !swiper.autoplay) {
				return;
			}

			swiper.params.speed = reducedMotion.matches ? 0 : transitionSpeed;

			if (reducedMotion.matches || swiper.isLocked || slidesCount <= 1) {
				stopAutoplay();
				updateControlsVisibility();
				return;
			}

			if (shouldPauseAutoplay()) {
				pauseAutoplay();
				updateControlsVisibility();
				return;
			}

			if (!swiper.autoplay.running) {
				swiper.autoplay.start();
			} else if (swiper.autoplay.paused) {
				swiper.autoplay.resume();
			}

			updateControlsVisibility();
		};

		swiper = new window.Swiper(slider, {
			slidesPerView: 1,
			slidesPerGroup: 1,
			spaceBetween: 24,
			loop: false,
			rewind: true,
			watchOverflow: true,
			speed: reducedMotion.matches ? 0 : transitionSpeed,
			autoplay: {
				delay: autoplayDelay,
				disableOnInteraction: false,
				pauseOnMouseEnter: true,
			},
			navigation: {
				prevEl,
				nextEl,
			},
			pagination: {
				el: paginationEl,
				clickable: true,
				dynamicBullets: true,
				dynamicMainBullets: 3,
			},
			keyboard: {
				enabled: true,
				onlyInViewport: true,
			},
			a11y: {
				enabled: true,
				prevSlideMessage: prevEl.getAttribute('aria-label') || '',
				nextSlideMessage: nextEl.getAttribute('aria-label') || '',
				firstSlideMessage: slider.dataset.cardSliderFirstMessage || '',
				lastSlideMessage: slider.dataset.cardSliderLastMessage || '',
				paginationBulletMessage: slider.dataset.cardSliderPaginationMessage || '',
			},
			breakpoints: {
				768: {
					slidesPerView: 2,
					slidesPerGroup: 2,
				},
				1200: {
					slidesPerView: 3,
					slidesPerGroup: 3,
				},
			},
		});

		slider.dataset.swiperInitialized = 'true';

		swiper.on('lock', reconcileAutoplay);
		swiper.on('unlock', reconcileAutoplay);
		swiper.on('breakpoint', reconcileAutoplay);
		swiper.on('transitionEnd', reconcileAutoplay);

		section.addEventListener('focusin', reconcileAutoplay);
		section.addEventListener('focusout', () => {
			window.requestAnimationFrame(() => {
				reconcileAutoplay();
			});
		});

		document.addEventListener('visibilitychange', reconcileAutoplay);

		if (typeof reducedMotion.addEventListener === 'function') {
			reducedMotion.addEventListener('change', reconcileAutoplay);
		} else {
			reducedMotion.addListener(reconcileAutoplay);
		}

		reconcileAutoplay();
	});
}
